/**
 * CRA3D Resume Studio — Phase 3 upload / paste / extraction-review page.
 *
 * Route: /resume-studio/:resumeId/upload
 *
 * Flow (per Mark's Phase 3 auth):
 *   1. Pick PDF / DOCX file OR paste text.
 *   2. POST /resume-studio/uploads → server extracts text (subprocess-
 *      isolated, macros / OLE / altChunk / encrypted / oversized all
 *      rejected).
 *   3. Extracted paragraphs shown in the LEFT panel — clearly labelled
 *      "Detected — NOT saved as claims yet".
 *   4. User assigns lines to sections via per-section textareas on the
 *      RIGHT — this is the review + confirmation step.
 *   5. POST /uploads/{id}/confirm → server creates user_uploaded
 *      claims (approved_by_user=True), links back to the resume.
 *   6. Navigate to /resume-studio/:resumeId (editor) — the new claims
 *      appear with the "You uploaded this" provenance pill.
 *
 * Extracted-but-not-confirmed text never becomes a claim.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Upload, FileText, ClipboardPaste, ArrowLeft, Check, CircleAlert,
  ShieldCheck, Trash2,
} from "lucide-react";

const SECTION_LABEL = {
  summary:              "Summary",
  employment_history:   "Employment history",
  education:            "Education",
  skills_technical:     "Technical skills",
  skills_transferable:  "Transferable skills",
  projects:             "Projects",
  certifications:       "Certifications",
};

// Sections users typically want to fill from an uploaded resume. Kept
// in a fixed order so the review UI is predictable across resumes.
const REVIEW_SECTIONS = [
  "summary", "employment_history", "education",
  "skills_technical", "skills_transferable", "projects",
  "certifications",
];

export default function ResumeStudioUpload() {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState("file"); // file | paste
  const [file, setFile] = useState(null);
  const [pasted, setPasted] = useState("");
  const [uploading, setUploading] = useState(false);
  const [upload, setUpload] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [assignments, setAssignments] = useState({}); // section_kind → string
  const fileInputRef = useRef(null);

  const doUpload = async () => {
    setError(null); setUpload(null); setAssignments({});
    if (mode === "file") {
      if (!file) { setError("Choose a PDF or DOCX file first."); return; }
      if (file.size > 5 * 1024 * 1024) {
        setError("That file is over the 5 MB upload limit.");
        return;
      }
      const contentType = file.type
        || (file.name.toLowerCase().endsWith(".pdf") ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
      if (!["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(contentType)) {
        setError("Please choose a PDF or DOCX file.");
        return;
      }
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file, file.name);
      try {
        const { data } = await api.post("/resume-studio/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setUpload(data.upload);
      } catch (err) {
        setError(extractApiError(err, "Upload rejected."));
      } finally {
        setUploading(false);
      }
    } else {
      if (!pasted.trim()) { setError("Paste something first."); return; }
      setUploading(true);
      try {
        const { data } = await api.post("/resume-studio/uploads/paste",
          { content: pasted });
        setUpload(data.upload);
      } catch (err) {
        setError(extractApiError(err, "Paste rejected."));
      } finally {
        setUploading(false);
      }
    }
  };

  const doConfirm = async () => {
    if (!upload) return;
    const section_entries = {};
    let total = 0;
    for (const kind of REVIEW_SECTIONS) {
      const text = (assignments[kind] || "").trim();
      if (!text) continue;
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      section_entries[kind] = lines;
      total += lines.length;
    }
    if (total === 0) {
      setError("Assign at least one line to a section before confirming.");
      return;
    }
    setConfirming(true);
    setError(null);
    try {
      await api.post(`/resume-studio/uploads/${upload.id}/confirm`,
        { resume_id: resumeId, section_entries });
      toast.success(`${total} entries added to your resume.`);
      navigate(`/resume-studio/${resumeId}`);
    } catch (err) {
      setError(extractApiError(err, "Confirm failed."));
    } finally {
      setConfirming(false);
    }
  };

  const doDiscard = async () => {
    if (!upload) return;
    try {
      await api.delete(`/resume-studio/uploads/${upload.id}`);
      toast.success("Upload discarded.");
      setUpload(null); setAssignments({}); setFile(null); setPasted("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error(extractApiError(err, "Discard failed."));
    }
  };

  const paragraphs = useMemo(
    () => upload?.extracted?.paragraphs || [],
    [upload],
  );

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
           data-testid="resume-studio-upload-page">
        <button
          onClick={() => navigate(`/resume-studio/${resumeId}`)}
          data-testid="resume-studio-upload-back"
          className="mb-4 text-xs text-fg-secondary hover:text-fg-primary flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to editor
        </button>
        <p className="text-xs uppercase tracking-wider text-fg-secondary">
          Resume Studio
        </p>
        <h1 data-testid="resume-studio-upload-title"
             className="mt-1 text-3xl sm:text-4xl font-heading font-medium text-fg-primary flex items-center gap-2">
          <Upload className="h-6 w-6" aria-hidden="true" />
          Add from an existing resume
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-fg-secondary">
          Upload a PDF or DOCX (5 MB max), or paste text directly. We
          extract the words, show them to you, and only add what you
          confirm to your resume. Nothing from this file becomes a
          claim on your resume until you approve each line.
        </p>

        <Alert
          variant="default"
          data-testid="resume-studio-upload-safety-notice"
          className="mt-6 border-progress-teal/30 bg-progress-teal/5 text-fg-primary"
        >
          <AlertDescription className="text-xs">
            <ShieldCheck className="h-4 w-4 mr-2 -mt-0.5 inline-block text-progress-teal"
                          aria-hidden="true" />
            Uploads run through a hardened path: file signature is
            verified, macros / OLE objects / encrypted PDFs are refused,
            parsing runs in an isolated subprocess with memory + CPU
            limits, and files are stored private-to-you (never public).
          </AlertDescription>
        </Alert>

        {error && (
          <Alert variant="destructive" data-testid="resume-studio-upload-error"
                  className="mt-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!upload ? (
          <section className="mt-8 space-y-6">
            <div
              role="tablist"
              className="flex gap-2 p-1 rounded-md bg-bg-surface border border-border-subtle w-fit"
            >
              <button
                onClick={() => setMode("file")}
                data-testid="resume-studio-upload-tab-file"
                data-selected={mode === "file"}
                className={"px-3 py-1.5 text-xs rounded transition-colors duration-150 " +
                  (mode === "file"
                    ? "bg-accent-warm text-bg-base"
                    : "text-fg-secondary hover:text-fg-primary")}
              >
                <FileText className="h-3.5 w-3.5 mr-1 -mt-0.5 inline-block"
                           aria-hidden="true" />
                Upload PDF / DOCX
              </button>
              <button
                onClick={() => setMode("paste")}
                data-testid="resume-studio-upload-tab-paste"
                data-selected={mode === "paste"}
                className={"px-3 py-1.5 text-xs rounded transition-colors duration-150 " +
                  (mode === "paste"
                    ? "bg-accent-warm text-bg-base"
                    : "text-fg-secondary hover:text-fg-primary")}
              >
                <ClipboardPaste className="h-3.5 w-3.5 mr-1 -mt-0.5 inline-block"
                                 aria-hidden="true" />
                Paste text
              </button>
            </div>

            {mode === "file" ? (
              <div className="rounded-lg border border-border-subtle bg-bg-surface p-6">
                <p className="text-sm font-medium text-fg-primary">
                  Choose a resume file
                </p>
                <p className="mt-1 text-xs text-fg-secondary">
                  PDF or DOCX. 5 MB max. Encrypted, macro-enabled, or
                  files with external references are refused.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="resume-studio-upload-file-input"
                  className="mt-4 block w-full text-sm text-fg-primary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-accent-warm file:text-bg-base hover:file:bg-accent-warm-hover"
                />
                {file && (
                  <p className="mt-3 text-xs text-fg-muted"
                     data-testid="resume-studio-upload-file-info">
                    {file.name} · {(file.size / 1024).toFixed(1)} KB
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-border-subtle bg-bg-surface p-6">
                <p className="text-sm font-medium text-fg-primary">
                  Paste your resume text
                </p>
                <p className="mt-1 text-xs text-fg-secondary">
                  Up to 200 KB. One entry per line typically works best.
                </p>
                <Textarea
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                  maxLength={200 * 1024}
                  data-testid="resume-studio-upload-paste-textarea"
                  className="mt-3 bg-bg-base border-border-subtle text-fg-primary min-h-48 font-mono text-xs"
                  placeholder="Paste your resume text here…"
                />
                <p className="mt-2 text-[11px] text-fg-muted"
                   data-testid="resume-studio-upload-paste-size">
                  {new Blob([pasted]).size.toLocaleString()} / 204,800 bytes
                </p>
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                onClick={() => navigate(`/resume-studio/${resumeId}`)}
                variant="ghost"
                data-testid="resume-studio-upload-cancel-btn"
                className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
              >
                Cancel
              </Button>
              <Button
                onClick={doUpload}
                disabled={uploading || (mode === "file" ? !file : !pasted.trim())}
                data-testid="resume-studio-upload-submit-btn"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                {uploading
                  ? "Extracting…"
                  : mode === "file" ? "Upload and extract" : "Extract from pasted text"}
              </Button>
            </div>
          </section>
        ) : (
          <section className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6"
                    data-testid="resume-studio-upload-review">
            <div className="rounded-lg border border-border-subtle bg-bg-surface p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-fg-secondary">Detected</p>
                  <h2 className="mt-1 text-lg font-heading font-medium text-fg-primary">
                    Text extracted from your file
                  </h2>
                  <p className="mt-1 text-xs text-fg-muted">
                    <CircleAlert className="h-3 w-3 -mt-0.5 inline-block text-accent-warm"
                                  aria-hidden="true" />{" "}
                    <strong>Not saved as claims yet.</strong> Copy the
                    lines you want into the section boxes on the right.
                  </p>
                </div>
                <Button
                  size="sm" variant="ghost"
                  onClick={doDiscard}
                  data-testid="resume-studio-upload-discard-btn"
                  className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                  Discard
                </Button>
              </div>
              <div className="mt-4 flex-1 max-h-[60vh] overflow-y-auto bg-bg-base border border-border-subtle rounded-md p-3 space-y-1 text-sm text-fg-primary"
                    data-testid="resume-studio-upload-paragraphs">
                {paragraphs.length === 0 ? (
                  <p className="text-fg-muted text-xs italic">
                    No text extracted. Try pasting the resume text instead.
                  </p>
                ) : paragraphs.map((line, i) => (
                  <p
                    key={i}
                    data-testid={`resume-studio-upload-line-${i}`}
                    className="border-l-2 border-border-subtle/40 pl-2 select-text hover:bg-bg-surface-hover cursor-text"
                    onClick={(e) => {
                      // Convenience: click a line to copy it to clipboard.
                      try {
                        navigator.clipboard.writeText(e.currentTarget.innerText);
                        toast.success("Copied. Paste it into a section on the right.");
                      } catch {}
                    }}
                  >
                    {line}
                  </p>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-fg-secondary">Review + assign</p>
                <h2 className="mt-1 text-lg font-heading font-medium text-fg-primary">
                  Which section does each line belong in?
                </h2>
                <p className="mt-1 text-xs text-fg-secondary">
                  Paste or type each line into the right section. One
                  entry per line. Blank sections stay empty.
                </p>
              </div>
              {REVIEW_SECTIONS.map((kind) => (
                <div key={kind}
                     data-testid={`resume-studio-upload-section-${kind}`}
                     className="rounded-md border border-border-subtle bg-bg-surface p-3">
                  <p className="text-sm font-medium text-fg-primary">
                    {SECTION_LABEL[kind] || kind}
                  </p>
                  <Textarea
                    value={assignments[kind] || ""}
                    onChange={(e) => setAssignments((a) => ({ ...a, [kind]: e.target.value }))}
                    data-testid={`resume-studio-upload-section-${kind}-input`}
                    className="mt-2 bg-bg-base border-border-subtle text-fg-primary min-h-20 text-sm"
                    placeholder="One entry per line. Blank sections stay empty."
                    maxLength={2000 * 20}
                  />
                </div>
              ))}
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate(`/resume-studio/${resumeId}`)}
                  data-testid="resume-studio-upload-cancel-review-btn"
                  className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                >
                  Cancel
                </Button>
                <Button
                  onClick={doConfirm}
                  disabled={confirming}
                  data-testid="resume-studio-upload-confirm-btn"
                  className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                >
                  <Check className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                  {confirming ? "Adding…" : "Add these to my resume"}
                </Button>
              </div>
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
